import {IProductPrice, IProductShort} from 'modules/products/types';
import React, {FC, memo, useMemo} from 'react';
import {TUsedIn} from 'modules/price-n-bonuses/types';
import {USED_IN} from 'modules/price-n-bonuses/constants';
import style from './style.module.scss';
import classNames from 'classnames';
import {Typography} from 'components/Typography';
import {formatPrice} from 'core/helpers';
import {Shield} from 'components/Shield';
import {OnlineEcomLabel} from 'modules/product-card/components/OnlineEcomLabel';
import {CashbackLabel} from 'components/CashbackLabel';
import cs from 'classnames';

export interface IProps extends IProductPrice {
    className?: string;
    usedIn: TUsedIn;
    isBasePriceSaleShield?: boolean;
    qtyPerUOM?: IProductShort['qtyPerUOM'];
    isOutOfStock?: boolean;
    labels: IProductShort['labels'];
    isHorizontalView?: boolean;
}

export const PriceAndBonuses: FC<IProps> = memo(
    // eslint-disable-next-line complexity
    ({
        className,
        usedIn = USED_IN.MAIN_OFFER,
        qtyPerUOM,
        base,
        discount,
        isSpecPrice = false,
        piece,
        specPrice,
        showDiscountPercent,
        isOutOfStock = false,
        isHorizontalView = false,
        isOnlineEcom,
        cashback,
        labels = [],
    }) => {
        const {discountValue, price: discountPrice} = discount || {};
        const isDiscountCheaper = specPrice && discountPrice < specPrice;
        const isDiscountPrice = !specPrice || isDiscountCheaper;
        const isSale = 0 < discountValue || specPrice;
        const onlineOnlyLabel = labels.find((label) => 'ONLINE_ONLY' === label.code);

        const basePrice = formatPrice(String(base?.price));
        const priceWithDiscount = formatPrice(String(discountPrice));
        const specialPrice = formatPrice(String(specPrice));
        const piecePrice = formatPrice(String(piece?.price));

        const salePrices = useMemo(
            () => (
                <>
                    <div
                        className={classNames('old-price-wrapper', style.oldPriceWrapper, {
                            [style.isPercent]: isSale,
                            [style.oldPriceWrapperHorizontal]: isHorizontalView,
                        })}
                    >
                        <div className={style.priceWithDiscount}>
                            <Typography className={'old-price'} variant={'label-12-r-lt'} color={'gray60'}>
                                {basePrice} ₽
                            </Typography>

                            {showDiscountPercent && (
                                <>
                                    <Shield backColor={'bordeaux'} size={'medium'} className={style.shieldOnlyDesktop}>
                                        -{discount.discountValue}%
                                    </Shield>
                                    <Shield backColor={'bordeaux'} size={'small'}>
                                        -{discount.discountValue}%
                                    </Shield>
                                </>
                            )}
                        </div>
                        {isOnlineEcom && isHorizontalView && <OnlineEcomLabel isSmall={false} />}
                        {isSpecPrice && isHorizontalView && (
                            <Typography className={style.specPriceTitle} element={'div'} variant={'label-12-r'}>
                                Спеццена
                            </Typography>
                        )}
                        {qtyPerUOM && 1 < qtyPerUOM && isHorizontalView && (
                            <Typography variant={'p-small'}>Цена за штуку {piecePrice} ₽</Typography>
                        )}
                        <CashbackLabel value={cashback} className={cs(style.cashback, style.cashbackDt)} />
                    </div>
                    <div className={classNames(style.pricePreviewWrapper, 'price-preview-wrapper')}>
                        <Typography
                            className={classNames(style.newPrice, 'new-price', {
                                [style.newPricePercent]: isSale,
                                ['new-price-sale-percent']: isSale,
                            })}
                            variants={{
                                DT: 'price-24-b-s',
                                TB: 'price-20-b',
                                MB: 'price-20-b',
                            }}
                        >
                            {isDiscountPrice ? priceWithDiscount : specialPrice} ₽
                        </Typography>
                        <CashbackLabel value={cashback} className={cs(style.cashback, style.cashbackTb)} />
                    </div>
                </>
            ),
            [
                isSale,
                isHorizontalView,
                basePrice,
                showDiscountPercent,
                discount.discountValue,
                isSpecPrice,
                qtyPerUOM,
                piecePrice,
                cashback,
                isDiscountPrice,
                priceWithDiscount,
                specialPrice,
                isOnlineEcom,
            ],
        );

        return (
            <div
                className={classNames(className, style.container, {
                    [style[usedIn]]: usedIn,
                    [style.containerHorizontal]: isHorizontalView,
                })}
                data-skeleton-item={true}
            >
                <div className={classNames('main-price', style.content)}>
                    {qtyPerUOM && 1 < qtyPerUOM && !isHorizontalView && (
                        <Typography variant={'p-small'}>Цена за штуку {piecePrice} ₽</Typography>
                    )}
                    {onlineOnlyLabel && (
                        <Typography color={'special-blue'} variant={'p-small'}>
                            Только онлайн
                        </Typography>
                    )}
                    {(isSpecPrice || isOnlineEcom) && (
                        <div className={style.specBlock}>
                            {isSpecPrice && !isHorizontalView && (
                                <Typography className={style.specPriceTitle} element={'div'} variant={'label-12-r'}>
                                    Спеццена
                                </Typography>
                            )}
                            {isOnlineEcom && !isHorizontalView && <OnlineEcomLabel isSmall={false} />}
                        </div>
                    )}
                    {isSale && !isOutOfStock ? (
                        salePrices
                    ) : (
                        <div
                            className={classNames(style.priceMainContainer, {
                                [style.priceMainContainerHorizontal]: isHorizontalView,
                            })}
                        >
                            <Typography
                                className={classNames(style.priceMain, 'base-price')}
                                variants={{
                                    DT: 'price-24-b-s',
                                    TB: 'price-20-b',
                                    MB: 'price-20-b',
                                }}
                            >
                                {basePrice} ₽
                            </Typography>
                            <CashbackLabel value={cashback} className={style.cashback} />
                        </div>
                    )}
                </div>
                {/* <Main {...price} isBasePriceSaleShield={isBasePriceSaleShield} quantity={quantity} usedIn={usedIn} />*/}
            </div>
        );
    },
    // eslint-disable-next-line complexity
    (prev, next) => {
        return (
            prev.usedIn === next.usedIn &&
            prev.qtyPerUOM === next.qtyPerUOM &&
            prev.isBasePriceSaleShield === next.isBasePriceSaleShield &&
            JSON.stringify(prev.base) === JSON.stringify(next.base) &&
            JSON.stringify(prev.discount) === JSON.stringify(next.discount) &&
            prev.hasEcomRule === next.hasEcomRule &&
            prev.isSpecPrice === next.isSpecPrice &&
            prev.specPrice === next.specPrice &&
            prev.showDiscountPercent === next.showDiscountPercent &&
            prev.specPrice === next.specPrice &&
            prev.isOutOfStock === next.isOutOfStock
        );
    },
);
